import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { FieldDef } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { Dictionary } from 'app/shared/models/dictionary';
import { SelectFieldComponent } from '@shared/components/widgets/select-field/select-field.component';

@Component({
  selector: 'app-field-layout-edit',
  templateUrl: './field-layout-edit.component.html',
  styleUrls: ['./field-layout-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldLayoutEditComponent {
  private fieldsMemento: string;

  currentFields: string[][];

  @Input() data: { fieldDefs: FieldDef[] };
  @Input() set fields(value: string[][]) {
    this.fieldsMemento = JSON.stringify(value);
    this.currentFields = JSON.parse(this.fieldsMemento);
  }
  @Input() filterFieldsBy: RelatedToType;
  @Input() ui: { title: string };
  @Input() hasNestedFiltersOn = false;
  @Output() onCancel = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<string[][]>();

  @ViewChild('listGroup') private listGroup: ElementRef<HTMLDivElement>;

  private get listGroupHTMLElement(): HTMLDivElement {
    return this.listGroup.nativeElement;
  }

  private get listGroupWidthScrollValue(): number {
    const { clientWidth, scrollWidth } = this.listGroupHTMLElement;
    return scrollWidth - clientWidth;
  }

  get hasChanged(): boolean {
    return JSON.stringify(this.currentFields) !== this.fieldsMemento;
  }

  get hasAnyFieldsAdded(): boolean {
    return this.currentFields?.reduce((p, c) => [...p, ...c], []).length > 0;
  }

  lookupField(name: string): FieldDef {
    if (name === SelectFieldComponent.emptyFieldDef.name) return SelectFieldComponent.emptyFieldDef;

    return this.data.fieldDefs.find((fieldDef) => fieldDef.name == name);
  }

  dropped({ container, currentIndex, previousContainer, previousIndex }: CdkDragDrop<any[]>): void {
    if (previousContainer === container) {
      moveItemInArray(container.data, previousIndex, currentIndex);
    } else {
      transferArrayItem(previousContainer.data, container.data, previousIndex, currentIndex);
    }
  }

  onFieldSelectionChange(event: Dictionary<string>, row: number, column: number): void {
    if (event) {
      const [firstProp] = Object.keys(event);
      this.currentFields[row][column] = event[firstProp];
    }
  }

  removeField(row: number, column: number): void {
    this.currentFields[row].splice(column, 1);

    if (!this.currentFields[row].length || this.currentFields[row].every((item) => !item)) {
      this.currentFields.splice(row, 1);
    }
  }

  addField(row: number): void {
    const field = this.data.fieldDefs.find((value) => !this.currentFields[row].includes(value.name));
    this.currentFields[row].push(field?.name ?? 'id');

    if (this.currentFields[row].length > 2) {
      setTimeout(() => this.scrollTo('end'), 100);
    }
  }

  addRow(index: number): void {
    if (this.currentFields.length) {
      this.currentFields.splice(index + 1, 0, ['id']);
    } else {
      this.currentFields.push(['id']);
    }

    setTimeout(() => this.scrollTo('begin'), 100);
  }

  cancel(): void {
    this.onCancel.emit();
  }

  save(): void {
    this.onSave.emit(this.currentFields);
  }

  addFirstField(): void {
    this.currentFields = [['id']];
  }

  private scrollTo(where: 'begin' | 'end'): void {
    if (where === 'begin') {
      this.listGroupHTMLElement.scrollLeft = 0;
    } else {
      this.listGroupHTMLElement.scrollLeft += this.listGroupWidthScrollValue;
    }
  }
}
